import { useState, useEffect } from 'react';
import api from '../helpers/axios';

// Types
export interface ArticleType {
  articleTypeId: number;
  typeName: string;
  typeDescription: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string | null;
}

interface UseArticleTypesReturn {
  articleTypes: ArticleType[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for managing article types
 * Fetches active article types from the API and provides loading/error states
 */
export const useArticleTypes = (): UseArticleTypesReturn => {
  const [articleTypes, setArticleTypes] = useState<ArticleType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchArticleTypes = async (): Promise<void> => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/article-types/active');
      
      // Validate response data
      if (Array.isArray(response.data)) {
        setArticleTypes(response.data);
      } else {
        throw new Error('Invalid response format: expected array of article types');
      }
    } catch (err) {
      console.error('Error fetching article types:', err);
      
      // Set user-friendly error message
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Failed to load article types. Please try again.');
      }
      
      // Reset article types on error
      setArticleTypes([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch article types on hook initialization
  useEffect(() => {
    fetchArticleTypes();
  }, []);

  return {
    articleTypes,
    isLoading,
    error,
    refetch: fetchArticleTypes,
  };
};

export default useArticleTypes;
